import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { TranslationService } from '../../services/translation.service';
import ValidationInfo from '../../validation/validation-info';
import TravelInformation, { type ITravelInformationState, type ITravelInformation } from './components/TravelInformation';
import ResultWithGraph from './components/ResultWithGraph';
import CarInformation, { type ICarInformationState } from './components/CarInformation';
import ValidationSummary from '../Shared/ValidationSummary';
import type IValidatable from '../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../helpers/validation.helper';
import { CarInformationDefault } from './constants/car-information.constants';
import StepIndicator from './components/StepIndicator';
import CalculationSummary from './components/CalculationSummary';
import { AnalyticsService } from '../../services/analytics-service';
import { CarUser } from './models/car-user.enum';
import { VariableCarCostsCalculator } from './helpers/variable-car-costs.calculator';
import { CarInformationField } from './models/car-information-field.enum';
import { TravelInformationField } from './models/travel-information-field.enum';
import type { ICarInformation } from './models/car-information.model';
import { StepComponentType } from './step-component-type.enum';
import { defaultTravelInformation } from './components/TravelInformation/default-travel-information.const';

function PriceCalculatorPage() {
  // === STATE ===
  const [currentStepState, setCurrentStepState] = useState<StepComponentType>(StepComponentType.TravelInformationStep);
  const [showDetails, setShowDetails] = useState<boolean>(false);

  const [travelInformationState, setTravelInformationState] = useState<ITravelInformationState>({
    value: defaultTravelInformation,
    validationInfo: new ValidationInfo(),
  });

  const [carInformationState, setCarInformationState] = useState<ICarInformationState>({
    value: CarInformationDefault,
    validationInfo: new ValidationInfo(),
  });

  const [updateResult, setUpdateResult] = useState(false);

  // === REFS ===
  const resultRef = useRef<HTMLDivElement | null>(null);
  const travelInformationSelectorRef = useRef<IValidatable>(null!);
  const carInformationSelectorRef = useRef<IValidatable>(null!);
  const headingAndStepRef = useRef<HTMLDivElement | null>(null);

  // === HELPER FUNCTIONS ===
  function showFeedbackForm() {
    const feedbackForm = document.querySelector('.price-calculator-feedback-form');
    if (feedbackForm) feedbackForm.classList.remove('d-none');
  }

  function getValidatableComponentsTravelInformation(): ValidatableComponent[] {
    return [{ component: travelInformationSelectorRef.current, validationInfo: travelInformationState.validationInfo, setState: setTravelInformationState }];
  }

  function createStepFlowAriaLiveText() {
    if (currentStepState === StepComponentType.TravelInformationStep) {
      return `${TranslationService.get('pricecalculatorpage.travelinformationheading')}, ${TranslationService.get('pricecalculatorpage.travelinformation.stepinflow')}`;
    }
    if (currentStepState === StepComponentType.CarInformationStep) {
      return `${TranslationService.get('pricecalculatorpage.carinformationheading')}. ${!showDetails ? TranslationService.get('pricecalculatorpage.carinformation.stepinflow') : ''}`;
    }
    return '';
  }

  function handleTravelInformationStateChange(updatedValue: ITravelInformation, changedParameter: TravelInformationField) {
    setTravelInformationState((ps) => ({ ...ps, value: updatedValue }));
    if (
      (changedParameter === TravelInformationField.DistanceFromHomeToWorkKmState && updatedValue.distanceFromHomeToWorkKmState) ||
      (changedParameter === TravelInformationField.TravelDaysPerMonth && updatedValue.travelDaysPerMonth)
    ) {
      setCarInformationState((ps) => ({
        ...ps,
        value: {
          ...ps.value,
          variableDepreciationPercentage:
            travelInformationState.value.distanceFromHomeToWorkKmState &&
            travelInformationState.value.travelDaysPerMonth &&
            carInformationState.value.drivingDistancePerYearKm &&
            updatedValue.distanceFromHomeToWorkKmState &&
            updatedValue.travelDaysPerMonth
              ? VariableCarCostsCalculator.calculateDistanceRatio(
                  changedParameter === TravelInformationField.DistanceFromHomeToWorkKmState ? updatedValue.distanceFromHomeToWorkKmState : travelInformationState.value.distanceFromHomeToWorkKmState,
                  changedParameter === TravelInformationField.TravelDaysPerMonth ? updatedValue.travelDaysPerMonth : travelInformationState.value.travelDaysPerMonth,
                  carInformationState.value.drivingDistancePerYearKm
                )
              : 0,
          commutePercentage:
            travelInformationState.value.distanceFromHomeToWorkKmState &&
            travelInformationState.value.travelDaysPerMonth &&
            carInformationState.value.drivingDistancePerYearKm &&
            updatedValue.distanceFromHomeToWorkKmState &&
            updatedValue.travelDaysPerMonth
              ? VariableCarCostsCalculator.calculateDistanceRatio(
                  changedParameter === TravelInformationField.DistanceFromHomeToWorkKmState ? updatedValue.distanceFromHomeToWorkKmState : travelInformationState.value.distanceFromHomeToWorkKmState,
                  changedParameter === TravelInformationField.TravelDaysPerMonth ? updatedValue.travelDaysPerMonth : travelInformationState.value.travelDaysPerMonth,
                  carInformationState.value.drivingDistancePerYearKm
                )
              : 0,
        },
      }));
    }
  }

  function handleCarInformationStateChange(updatedValue: ICarInformation, changedParameter: CarInformationField) {
    let updatedVariableDepreciationPercentage = updatedValue.variableDepreciationPercentage;
    let updatedCommutePercentage = updatedValue.commutePercentage;

    if (
      changedParameter === CarInformationField.DrivingDistancePerYearKm &&
      travelInformationState.value.distanceFromHomeToWorkKmState &&
      travelInformationState.value.travelDaysPerMonth &&
      updatedValue.drivingDistancePerYearKm
    ) {
      updatedVariableDepreciationPercentage = VariableCarCostsCalculator.calculateDistanceRatio(
        travelInformationState.value.distanceFromHomeToWorkKmState,
        travelInformationState.value.travelDaysPerMonth,
        updatedValue.drivingDistancePerYearKm
      );
      updatedCommutePercentage = VariableCarCostsCalculator.calculateDistanceRatio(
        travelInformationState.value.distanceFromHomeToWorkKmState,
        travelInformationState.value.travelDaysPerMonth,
        updatedValue.drivingDistancePerYearKm
      );
    }

    setCarInformationState((ps) => ({
      ...ps,
      value: {
        ...ps.value,
        ...updatedValue,
        variableDepreciationPercentage: updatedVariableDepreciationPercentage,
        commutePercentage: updatedCommutePercentage,
      },
    }));
  }

  // NV-6014: If the distance to work and home per year exceeds the driving distance per year, use distance to work and home per year
  const handleUsedDistanceInCalculationValueChanged = (value: number) => {
    setCarInformationState((ps) => ({ ...ps, value: { ...ps.value, drivingDistancePerYearKm: value } }));
  };

  function getValidatableComponentsCarInformation(): ValidatableComponent[] {
    return [{ component: carInformationSelectorRef.current, validationInfo: carInformationState.validationInfo, setState: setCarInformationState }];
  }

  // === VARIABLES ===
  const validationSummaryErrorsTravelInformation: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponentsTravelInformation()).filter((e) => e.length > 0);
  const validationSummaryErrorsCarInformation: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponentsCarInformation()).filter((e) => e.length > 0);

  // === EFFECTS ===
  useEffect(() => {
    // Add the initial step to the browser's history
    window.history.replaceState({ step: currentStepState }, document.title, window.location.href);

    const popstateListener = (event: PopStateEvent) => {
      event.preventDefault();

      // If the state is null (user navigated back to the initial page state), set the step to TravelInformation
      if (!event.state) {
        setCurrentStepState(StepComponentType.TravelInformationStep);
      } else {
        // Otherwise, set the current step to the step stored in the state
        setCurrentStepState(event.state.step);
      }
    };

    window.addEventListener('popstate', popstateListener);

    // Clean up the listener when the component unmounts
    return () => {
      window.removeEventListener('popstate', popstateListener);
    };
  }, [currentStepState]);

  // === EVENT HANDLERS ===
  async function handleButtonClick(nextStep: StepComponentType, validate: boolean = true) {
    let isFormValid = true;

    if (validate) {
      await flushSync(async () => {
        if (currentStepState !== StepComponentType.CarInformationStep) {
          [isFormValid] = ValidationHelper.checkValidity(getValidatableComponentsTravelInformation());
        }

        if (currentStepState !== StepComponentType.TravelInformationStep) {
          if (!isFormValid) {
            ValidationHelper.checkValidity(getValidatableComponentsCarInformation());
          } else {
            [isFormValid] = ValidationHelper.checkValidity(getValidatableComponentsCarInformation());
          }
        }
      });

      if (!isFormValid) {
        ValidationHelper.scrollToAndFocusOnValidationSummary();
        return;
      }
    }

    setCurrentStepState(nextStep);
    // Add a new history entry with the new step as the state
    window.history.pushState({ step: nextStep }, document.title, window.location.href);
    if (nextStep === StepComponentType.ResultStep) {
      setUpdateResult(true);
      showFeedbackForm();
      setShowDetails(false);
      // NV-5959: Tracking performed when going to the next step or updating comparison since a default value is set
      // see https://github.com/devtrafik/web-vasttrafikse/pull/950#discussion_r1620764133
      AnalyticsService.addEvent('Priskalkylator', 'Val äger/leasar bil', carInformationState.value.carUser === CarUser.CarOwner ? 'Ägare' : 'Leasing');
    }

    // Scroll to the heading and step information section
    await flushSync(async () => {});

    if (headingAndStepRef.current) {
      headingAndStepRef.current.scrollIntoView({ behavior: 'smooth' });
      if (navigator.userAgent.indexOf('Firefox') !== -1 && resultRef.current) {
        resultRef.current.focus();
      }
    }
  }

  return (
    <div className="price-calculator-page">
      <div id="form-step-description" className="visually-hidden" aria-live="polite">
        {createStepFlowAriaLiveText()}
      </div>
      <div className="heading-and-step-information bg-blue">
        <div ref={headingAndStepRef} className="py-4 py-md-5">
          {currentStepState !== StepComponentType.ResultStep && (
            <>
              <div className="container container--narrower text-center mb-4 mb-md-5">
                <h1>{TranslationService.get('pricecalculatorpage.heading')}</h1>
                <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: TranslationService.get('pricecalculatorpage.preamble') }}></div>
              </div>
              <div className="container container--narrower">
                <StepIndicator currentStep={currentStepState} />
              </div>
            </>
          )}
          {currentStepState === StepComponentType.ResultStep && (
            <>
              <div ref={resultRef} tabIndex={-1} className="mb-4">
                <ResultWithGraph
                  travelInformation={travelInformationState}
                  carInformation={carInformationState}
                  updateResult={updateResult}
                  resetUpdateResult={() => setUpdateResult(false)}
                  usedDistanceInCalculationValueChanged={handleUsedDistanceInCalculationValueChanged}
                />
              </div>

              <div className="container container--narrower">
                {!showDetails && (
                  <div className="d-grid">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        AnalyticsService.addEvent(AnalyticsService.PriceCalculator, 'Klick på Lägg till fler uppgifter');
                        setShowDetails(true);
                      }}
                    >
                      {TranslationService.get('pricecalculatorpage.addmoredetails')}
                    </button>
                  </div>
                )}
                {showDetails && (
                  <>
                    {validationSummaryErrorsTravelInformation && <ValidationSummary validationSummaryErrors={validationSummaryErrorsTravelInformation.concat(validationSummaryErrorsCarInformation)} />}
                    <TravelInformation
                      state={travelInformationState}
                      onChange={handleTravelInformationStateChange}
                      showDetailsInformation={true}
                      ref={travelInformationSelectorRef}
                      className="mb-4"
                    ></TravelInformation>
                    <CarInformation state={carInformationState} onChange={handleCarInformationStateChange} showDetailsInformation={true} ref={carInformationSelectorRef} className="mb-4" />
                    <div className="d-grid">
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          AnalyticsService.addEvent(AnalyticsService.PriceCalculator, 'Klick på Uppdatera jämförelse');
                          handleButtonClick(StepComponentType.ResultStep, true);
                        }}
                      >
                        {TranslationService.get('pricecalculatorpage.updatecomparison')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {currentStepState === StepComponentType.ResultStep && (
        <div className="container container--narrower py-4 py-md-5">
          <CalculationSummary travelInformation={travelInformationState} carInformation={carInformationState} updateResult={updateResult} resetUpdateResult={() => setUpdateResult(false)} />
        </div>
      )}

      {currentStepState !== StepComponentType.ResultStep && (
        <div className="container container--narrower py-4 py-md-5">
          <div>
            {currentStepState === StepComponentType.TravelInformationStep && (
              <div>
                {validationSummaryErrorsTravelInformation && <ValidationSummary validationSummaryErrors={validationSummaryErrorsTravelInformation} />}
                <TravelInformation state={travelInformationState} onChange={handleTravelInformationStateChange} ref={travelInformationSelectorRef} className="mb-4" />
                <div className="d-grid">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      AnalyticsService.addEvent(AnalyticsService.PriceCalculator, 'Klick på Nästa i reseuppgifter');
                      handleButtonClick(StepComponentType.CarInformationStep);
                    }}
                  >
                    {TranslationService.get('pricecalculatorpage.next')}
                  </button>
                </div>
              </div>
            )}

            {currentStepState === StepComponentType.CarInformationStep && (
              <div>
                {validationSummaryErrorsCarInformation && <ValidationSummary validationSummaryErrors={validationSummaryErrorsCarInformation} />}
                <CarInformation state={carInformationState} onChange={handleCarInformationStateChange} ref={carInformationSelectorRef} className="mb-4" />
                <div className="row">
                  <div className="col-12 col-sm-6 order-2 order-sm-1">
                    <div className="d-grid">
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          AnalyticsService.addEvent(AnalyticsService.PriceCalculator, 'Klick på Föregående steg i fordonsuppgifter');
                          handleButtonClick(StepComponentType.TravelInformationStep, false);
                        }}
                      >
                        {TranslationService.get('pricecalculatorpage.previous')}
                      </button>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 mb-3 mb-sm-0 order-1 order-sm-2">
                    <div className="d-grid">
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          AnalyticsService.addEvent(AnalyticsService.PriceCalculator, 'Klick på Jämför kostnader i fordonsuppgifter');
                          handleButtonClick(StepComponentType.ResultStep);
                        }}
                      >
                        {TranslationService.get('pricecalculatorpage.comparecosts')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PriceCalculatorPage;
